import { Link } from 'react-router-dom';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const CREATE_SUBSCRIPTION = `# POST https://api.revvly.ai/api/v1/graphql — public GraphQL, WRITE capability required.
mutation CreateSubscription($input: CreateSubscriptionInput!) {
  createSubscription(input: $input) {
    id
    externalId
  }
}

{
  "input": {
    "clientExternalId": "acct_123",
    "bundleId":          "663b1f2e4a3c2d1e0f8b7a55",
    "period":            "MONTHLY",
    "startDate":         "2026-06-01T00:00:00.000Z",
    "externalId":        "acct_123"
  }
}`;

const AGENT_PROMPT = `You are implementing Revvly usage reporting without storing Revvly ids.
At subscription provisioning, set externalId to your own stable account or subscription id
via the public createSubscription mutation (WRITE-capable API key) — the internal dashboard
GraphQL provisioning flow (createSubscriptionWithPricing) is only needed for migrated or
otherwise advanced subscriptions. For later usage, send externalSubscriptionId with a
variableSlug; never send both the external and internal identifier for either the
subscription or meter.
Discover meter slugs through GET https://api.revvly.ai/api/v1/variables and use
GET https://api.revvly.ai/api/v1/subscriptions only when you need to sync or inspect ids.
Discovery requests use Bearer auth but no HMAC.
For POST https://api.revvly.ai/api/v1/events, unresolved references are per-event REJECTED
results inside HTTP 202.
For https://api.revvly.ai/api/v1/usage mutations, unresolved or invalid reference pairs are
normal HTTP errors.
Use the event or usage API's required idempotency mechanism; never put API secrets in browser code.`;

const REPORT_USAGE = `POST https://api.revvly.ai/api/v1/events
Authorization:      Bearer rvly_sk_live_<your-token>
Content-Type:       application/json
X-Signature-SHA256: sha256=<hmac-hex>

{
  "events": [
    {
      "eventId":                "01HYZ7K2N8V3WXPQ4JCBM5FGTE",
      "externalSubscriptionId": "acct_123",
      "variableSlug":           "api-calls",
      "type":                   "USAGE_REPORTED",
      "quantity":               1500,
      "reportedAt":             "2026-06-14T12:00:00Z"
    }
  ]
}`;

const LIST_SUBSCRIPTIONS = `GET https://api.revvly.ai/api/v1/subscriptions?status=ACTIVE&limit=100
Authorization: Bearer rvly_sk_live_<your-token>

{
  "data": [
    {
      "id":                   "663b1f2e4a3c2d1e0f8b7a9c",
      "externalId":           "acct_123",
      "organizationId":       "663b1f2e4a3c2d1e0f8b7a01",
      "status":               "ACTIVE",
      "period":               "MONTHLY",
      "currency":             "USD",
      "startDate":            "2026-04-23T00:00:00Z",
      "endDate":              null,
      "autoRenew":            true,
      "variableComponentIds": ["663b1f2e4a3c2d1e0f8b7b01"],
      "metadata":             { "tier": "pro" }
    }
  ],
  "nextCursor": "eyJpZCI6IjY2M2IxZjJl..."
}`;

const LIST_VARIABLES = `GET https://api.revvly.ai/api/v1/variables
Authorization: Bearer rvly_sk_live_<your-token>

{
  "data": [
    {
      "id":           "663b1f2e4a3c2d1e0f8b7b01",
      "slug":         "api-calls",
      "displayName":  "API calls",
      "variableType": "api-call",
      "unitLabel":    "call",
      "isActive":     true
    },
    {
      "id":           "663b1f2e4a3c2d1e0f8b7b02",
      "slug":         "seats",
      "displayName":  "User seats",
      "variableType": "seat",
      "unitLabel":    "seat",
      "isActive":     true
    }
  ]
}`;

const LIST_REGIONS = `GET https://api.revvly.ai/api/v1/regions
Authorization: Bearer rvly_sk_live_<your-token>

{
  "data": [
    {
      "id":                  "663b1f2e4a3c2d1e0f8b7a55",
      "name":                "United States",
      "code":                "US",
      "description":         null,
      "defaultCurrency":     "USD",
      "supportedCurrencies": ["USD"],
      "isActive":            true
    }
  ]
}`;

export function ExternalReferences() {
  useDocsPage('External References & ID Discovery — Revvly Docs');

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">External references</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Reference resources by <em>your own</em> ids.
        </h1>
        <p className="dp-oneliner">
          Identify subscriptions and variables with references you already own &mdash; or discover
          Revvly&rsquo;s ids on demand &mdash; so your integration never has to store an internal
          Mongo id.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/external-references')} />

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-plain">
          Use this context when an agent needs to connect your own account ids to Revvly metering.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      {/* ── The recommended flow ───────────────────────────────── */}
      <section className="dp-section">
        <h2>The recommended flow: reference by your own id</h2>
        <p className="dp-section-sub">
          Set <span className="api-inline">externalId</span> on the subscription when you create it
          &mdash; use your own account or subscription identifier. It is unique per tenant. From
          then on, report usage with <span className="api-inline">externalSubscriptionId</span> and
          identify the metric with <span className="api-inline">variableSlug</span>. You never read
          or store a Revvly id.
        </p>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <span className="api-inline">externalId</span> is unique per tenant. Setting it at
            creation gives you a stable handle for every later usage call &mdash; no lookup, no
            mapping table.
          </span>
        </div>

        <h3 className="api-sub-heading">1. Create the subscription with your id</h3>
        <p className="dp-section-sub">
          Pass <span className="api-inline">externalId</span> on the public{' '}
          <span className="api-inline">createSubscription</span> mutation &mdash; the same
          WRITE-capable API key you use for usage reporting can provision the subscription directly,
          right after creating its client with <span className="api-inline">createClient</span>. The
          dashboard&rsquo;s subscription wizard (
          <span className="api-inline">createSubscriptionWithPricing</span>, on the internal
          JWT-authed API) remains for migrations and other advanced authoring the public mutation
          deliberately leaves out &mdash; see{' '}
          <Link to="/docs/api/graphql-public#create-subscription">
            the public GraphQL reference
          </Link>{' '}
          for what that excludes. You can change <span className="api-inline">externalId</span>{' '}
          later via an update mutation, but the previous value is not kept as an alias &mdash;
          update every sender that reports{' '}
          <span className="api-inline">externalSubscriptionId</span> to the new value before you
          replace it, or usage against the old id will stop resolving.
        </p>
        <div className="api-code-wrap">
          <span className="api-code-label">GraphQL</span>
          <pre className="api-code">{CREATE_SUBSCRIPTION}</pre>
        </div>

        <h3 className="api-sub-heading">2. Report usage with your id</h3>
        <p className="dp-section-sub">
          Now every event references <span className="api-inline">acct_123</span> and the meter slug{' '}
          <span className="api-inline">api-calls</span> &mdash; no Revvly id anywhere. Supply
          exactly one of <span className="api-inline">subscriptionId</span> /{' '}
          <span className="api-inline">externalSubscriptionId</span> and exactly one of{' '}
          <span className="api-inline">variableId</span> /{' '}
          <span className="api-inline">variableSlug</span>.
        </p>
        <div className="api-code-wrap">
          <span className="api-code-label">POST https://api.revvly.ai/api/v1/events</span>
          <pre className="api-code">{REPORT_USAGE}</pre>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            An <span className="api-inline">externalSubscriptionId</span> or{' '}
            <span className="api-inline">variableSlug</span> that doesn&rsquo;t resolve to a
            resource in the API key&rsquo;s scope yields a per-event{' '}
            <span className="api-inline">REJECTED</span> result on the events batch (and a{' '}
            <span className="api-inline">404</span> / <span className="api-inline">400</span> on the
            single-item usage endpoints). See the{' '}
            <Link to="/docs/api/events">Event Ingestion API</Link> for the full payload and signing
            details.
          </span>
        </div>
      </section>

      {/* ── Sync and store alternative ─────────────────────────── */}
      <section className="dp-section">
        <h2>Alternative: sync and store Revvly ids</h2>
        <p className="dp-section-sub">
          If you prefer to keep Revvly&rsquo;s ids in your own database, read them once and cache
          them. Use{' '}
          <span className="api-inline">GET https://api.revvly.ai/api/v1/subscriptions</span> to list
          subscriptions and{' '}
          <span className="api-inline">GET https://api.revvly.ai/api/v1/variables</span> to map your
          metrics. These are GET requests &mdash; Bearer-authenticated but <strong>not</strong>{' '}
          HMAC-signed &mdash; and the results are filtered to the API key&rsquo;s scope.
        </p>

        <h3 className="api-sub-heading">List subscriptions</h3>
        <p className="dp-section-sub">
          Keyset pagination: follow <span className="api-inline">nextCursor</span> until it is
          absent. <span className="api-inline">limit</span> defaults to 100 and caps at
          1&thinsp;000.
        </p>
        <div className="api-code-wrap">
          <pre className="api-code">{LIST_SUBSCRIPTIONS}</pre>
        </div>

        <h3 className="api-sub-heading">List variables</h3>
        <p className="dp-section-sub">
          This is how you discover a <span className="api-inline">variableSlug</span> (or the
          internal <span className="api-inline">variableId</span>) to send on usage events. Pass{' '}
          <span className="api-inline">includeInactive=true</span> to also return deprecated
          variables.
        </p>
        <div className="api-code-wrap">
          <pre className="api-code">{LIST_VARIABLES}</pre>
        </div>

        <h3 className="api-sub-heading">List regions</h3>
        <p className="dp-section-sub">
          Regions carry the currencies a client can be billed in, and pin the currency of the
          subscriptions you report usage against. Only active regions are returned by default; pass{' '}
          <span className="api-inline">includeInactive=true</span> to also return archived ones.
        </p>
        <div className="api-code-wrap">
          <pre className="api-code">{LIST_REGIONS}</pre>
        </div>
      </section>

      {/* ── Discovery endpoint reference ───────────────────────── */}
      <section className="dp-section">
        <h2>Discovery endpoints</h2>
        <p className="dp-section-sub">
          All are read-only GETs under the same <span className="api-inline">rvly_sk_live_*</span>{' '}
          Bearer auth as the ingestion API. They are not HMAC-signed. Subscription and variable
          responses are filtered to the API key&rsquo;s scope; regions are scoped to your tenant.
        </p>

        <div className="api-endpoint-target">
          <AnchorHeading id="get-subscriptions" level={3} className="api-sub-heading">
            List subscriptions
          </AnchorHeading>
          <div className="api-endpoint-bar">
            <span className="api-method api-method-get">GET</span>
            <span className="api-url">https://api.revvly.ai/api/v1/subscriptions</span>
            <span className="api-returns">{'{ data: [...], nextCursor }'}</span>
          </div>
          <div className="dp-fields-table api-field-table-mb">
            <div className="dp-field-row">
              <div className="dp-field-k">Query params</div>
              <div className="dp-field-v">
                <span className="api-inline">organizationId</span>,{' '}
                <span className="api-inline">externalId</span>,{' '}
                <span className="api-inline">status</span>,{' '}
                <span className="api-inline">cursor</span>,{' '}
                <span className="api-inline">limit</span> &mdash; all optional. Keyset pagination
                via <span className="api-inline">cursor</span> /{' '}
                <span className="api-inline">nextCursor</span>;{' '}
                <span className="api-inline">limit</span> defaults to 100, caps at 1&thinsp;000.
              </div>
            </div>
            <div className="dp-field-row">
              <div className="dp-field-k">Response</div>
              <div className="dp-field-v">
                <span className="api-inline">data[]</span> of subscription summaries &mdash;{' '}
                <span className="api-inline">id</span>,{' '}
                <span className="api-inline">externalId</span>,{' '}
                <span className="api-inline">organizationId</span>,{' '}
                <span className="api-inline">status</span>,{' '}
                <span className="api-inline">period</span>,{' '}
                <span className="api-inline">currency</span>,{' '}
                <span className="api-inline">startDate</span>,{' '}
                <span className="api-inline">endDate</span>,{' '}
                <span className="api-inline">autoRenew</span>,{' '}
                <span className="api-inline">variableComponentIds</span>,{' '}
                <span className="api-inline">metadata</span> &mdash; plus{' '}
                <span className="api-inline">nextCursor</span>.
              </div>
            </div>
          </div>
        </div>

        <div className="api-endpoint-target">
          <AnchorHeading id="get-subscription" level={3} className="api-sub-heading">
            Get subscription by id
          </AnchorHeading>
          <div className="api-endpoint-bar">
            <span className="api-method api-method-get">GET</span>
            <span className="api-url">https://api.revvly.ai/api/v1/subscriptions/:id</span>
            <span className="api-returns">one summary</span>
          </div>
          <div className="dp-fields-table api-field-table-mb">
            <div className="dp-field-row">
              <div className="dp-field-k">Path param</div>
              <div className="dp-field-v">
                <span className="api-inline">id</span> &mdash; the internal Revvly subscription id.
              </div>
            </div>
            <div className="dp-field-row">
              <div className="dp-field-k">Response</div>
              <div className="dp-field-v">
                A single subscription summary (same shape as a{' '}
                <span className="api-inline">data[]</span> item above).
              </div>
            </div>
          </div>
        </div>

        <div className="api-endpoint-target">
          <AnchorHeading id="get-subscription-by-external" level={3} className="api-sub-heading">
            Get subscription by external id
          </AnchorHeading>
          <div className="api-endpoint-bar">
            <span className="api-method api-method-get">GET</span>
            <span className="api-url">
              https://api.revvly.ai/api/v1/subscriptions/by-external/:externalId
            </span>
            <span className="api-returns">one summary</span>
          </div>
          <div className="dp-fields-table api-field-table-mb">
            <div className="dp-field-row">
              <div className="dp-field-k">Path param</div>
              <div className="dp-field-v">
                <span className="api-inline">externalId</span> &mdash; the reference you set at
                creation. Resolves the same single subscription summary by your own id.
              </div>
            </div>
            <div className="dp-field-row">
              <div className="dp-field-k">Response</div>
              <div className="dp-field-v">
                A single subscription summary, or <span className="api-inline">404</span> if no
                subscription in scope carries that <span className="api-inline">externalId</span>.
              </div>
            </div>
          </div>
        </div>

        <div className="api-endpoint-target">
          <AnchorHeading id="get-variables" level={3} className="api-sub-heading">
            List variables
          </AnchorHeading>
          <div className="api-endpoint-bar">
            <span className="api-method api-method-get">GET</span>
            <span className="api-url">https://api.revvly.ai/api/v1/variables</span>
            <span className="api-returns">{'{ data: [...] }'}</span>
          </div>
          <div className="dp-fields-table api-field-table-mb">
            <div className="dp-field-row">
              <div className="dp-field-k">Query param</div>
              <div className="dp-field-v">
                <span className="api-inline">includeInactive</span> &mdash; optional; set to{' '}
                <span className="api-inline">true</span> to also return deprecated meters.
              </div>
            </div>
            <div className="dp-field-row">
              <div className="dp-field-k">Response</div>
              <div className="dp-field-v">
                <span className="api-inline">data[]</span> of meter summaries &mdash;{' '}
                <span className="api-inline">id</span>, <span className="api-inline">slug</span>,{' '}
                <span className="api-inline">displayName</span>,{' '}
                <span className="api-inline">variableType</span>,{' '}
                <span className="api-inline">unitLabel</span>,{' '}
                <span className="api-inline">isActive</span>. This is the source for the{' '}
                <span className="api-inline">variableSlug</span> you send on usage events.
              </div>
            </div>
          </div>
        </div>

        <div className="api-endpoint-target">
          <AnchorHeading id="get-regions" level={3} className="api-sub-heading">
            List regions
          </AnchorHeading>
          <div className="api-endpoint-bar">
            <span className="api-method api-method-get">GET</span>
            <span className="api-url">https://api.revvly.ai/api/v1/regions</span>
            <span className="api-returns">{'{ data: [...] }'}</span>
          </div>
          <div className="dp-fields-table api-field-table-mb">
            <div className="dp-field-row">
              <div className="dp-field-k">Query param</div>
              <div className="dp-field-v">
                <span className="api-inline">includeInactive</span> &mdash; optional; set to{' '}
                <span className="api-inline">true</span> to also return archived regions.
              </div>
            </div>
            <div className="dp-field-row">
              <div className="dp-field-k">Response</div>
              <div className="dp-field-v">
                <span className="api-inline">data[]</span> of region summaries &mdash;{' '}
                <span className="api-inline">id</span>, <span className="api-inline">name</span>,{' '}
                <span className="api-inline">code</span>,{' '}
                <span className="api-inline">description</span>,{' '}
                <span className="api-inline">defaultCurrency</span>,{' '}
                <span className="api-inline">supportedCurrencies</span>,{' '}
                <span className="api-inline">isActive</span>. The{' '}
                <span className="api-inline">id</span> is a client&rsquo;s{' '}
                <span className="api-inline">regionId</span>.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cross-links ────────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Event Ingestion API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/concepts/subscription"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              The Subscription concept ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
